// UltraFlex Admin - Finance Export (Permission-Gated)
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Download, FileText, CreditCard, Building2, Calendar,
  BarChart3, Lock, AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminFinanceExport = () => {
  const { token } = useAuth();
  const [hasAccess, setHasAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [dateRange, setDateRange] = useState({
    from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0],
  });
  const [downloading, setDownloading] = useState('');

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => { checkAccess(); fetchSites(); }, []);
  useEffect(() => { if (hasAccess) fetchSummary(); }, [hasAccess, selectedSite, dateRange]);

  const checkAccess = async () => {
    try {
      const res = await fetch(`${API_URL}/api/finance/check-access`, { headers });
      const data = await res.json();
      setHasAccess(data.has_access);
    } catch (e) { setHasAccess(false); }
    setLoading(false);
  };

  const fetchSites = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sites`, { headers });
      const data = await res.json();
      setSites(data.items || []);
    } catch (e) { console.error(e); }
  };

  const fetchSummary = async () => {
    try {
      const params = new URLSearchParams({ from_date: dateRange.from, to_date: dateRange.to });
      if (selectedSite) params.append('site_id', selectedSite);
      const res = await fetch(`${API_URL}/api/finance/summary?${params}`, { headers });
      const data = await res.json();
      setSummary(data);
    } catch (e) { console.error(e); }
  };

  const downloadExport = async (type) => {
    setDownloading(type);
    try {
      const params = new URLSearchParams({ from_date: dateRange.from, to_date: dateRange.to });
      if (selectedSite) params.append('site_id', selectedSite);
      const res = await fetch(`${API_URL}/api/finance/export/${type}?${params}`, { headers });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_export_${dateRange.from}_to_${dateRange.to}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
    setDownloading('');
  };

  if (loading) return <div className="p-8 text-center text-uf-text-muted">Checking access...</div>;

  if (!hasAccess) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]" data-testid="finance-no-access">
        <div className="text-center">
          <Lock className="w-16 h-16 text-uf-text-muted mx-auto mb-4" />
          <h2 className="text-xl font-bold text-uf-text mb-2">Access Restricted</h2>
          <p className="text-uf-text-muted max-w-md">
            You don't have permission to access Finance Exports. Contact an admin to grant you the <span className="font-mono text-uf-red">finance_export</span> permission.
          </p>
        </div>
      </div>
    );
  }

  const exports = [
    { id: 'billing', label: 'Billing Export', desc: 'All payment transactions with member details', icon: CreditCard },
    { id: 'collections', label: 'DD Collections', desc: 'Direct Debit collections with mandate references', icon: FileText },
    { id: 'reconciliation', label: 'Reconciliation', desc: 'Match active subscriptions against collections', icon: BarChart3 },
    { id: 'revenue-by-site', label: 'Revenue by Site', desc: 'Per-site revenue breakdown with member counts', icon: Building2 },
  ];

  return (
    <div className="p-4 lg:p-8" data-testid="finance-export-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-uf-text">Finance & Exports</h1>
          <p className="text-uf-text-muted text-sm mt-1">Download finance-ready reports and billing data</p>
        </div>
        <div className="flex items-center gap-3">
          <select data-testid="finance-site-filter" value={selectedSite} onChange={e => setSelectedSite(e.target.value)} className="bg-uf-surface border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm">
            <option value="">All Sites</option>
            {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <input type="date" value={dateRange.from} onChange={e => setDateRange({...dateRange, from: e.target.value})} className="bg-uf-surface border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" />
          <input type="date" value={dateRange.to} onChange={e => setDateRange({...dateRange, to: e.target.value})} className="bg-uf-surface border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" />
        </div>
      </div>

      {/* Revenue Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8" data-testid="finance-summary">
          <div className="bg-uf-surface rounded-xl border border-uf-border p-5">
            <p className="text-uf-text-muted text-xs uppercase mb-2">DD Collected</p>
            <p className="text-2xl font-bold text-green-400">£{summary.direct_debit?.collected?.toFixed(2) || '0.00'}</p>
          </div>
          <div className="bg-uf-surface rounded-xl border border-uf-border p-5">
            <p className="text-uf-text-muted text-xs uppercase mb-2">DD Failed</p>
            <p className="text-2xl font-bold text-red-400">£{summary.direct_debit?.failed?.toFixed(2) || '0.00'}</p>
          </div>
          <div className="bg-uf-surface rounded-xl border border-uf-border p-5">
            <p className="text-uf-text-muted text-xs uppercase mb-2">Card Payments</p>
            <p className="text-2xl font-bold text-blue-400">£{summary.card_payments?.toFixed(2) || '0.00'}</p>
          </div>
          <div className="bg-uf-surface rounded-xl border border-uf-border p-5">
            <p className="text-uf-text-muted text-xs uppercase mb-2">Total Revenue</p>
            <p className="text-2xl font-bold text-uf-text">£{summary.total_revenue?.toFixed(2) || '0.00'}</p>
          </div>
        </div>
      )}

      {/* Export Downloads */}
      <h2 className="text-lg font-semibold text-uf-text mb-4">Available Exports</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {exports.map(ex => (
          <div key={ex.id} className="bg-uf-surface rounded-xl border border-uf-border p-5 flex items-start gap-4 hover:border-uf-red/50 transition-colors">
            <div className="p-3 bg-uf-red/10 rounded-lg">
              <ex.icon className="w-6 h-6 text-uf-red" />
            </div>
            <div className="flex-1">
              <h3 className="text-uf-text font-semibold">{ex.label}</h3>
              <p className="text-uf-text-muted text-sm mt-1">{ex.desc}</p>
              <button data-testid={`download-${ex.id}`} onClick={() => downloadExport(ex.id)}
                disabled={downloading === ex.id}
                className="mt-3 flex items-center gap-2 text-uf-red hover:text-red-400 text-sm font-medium disabled:opacity-50">
                <Download className="w-4 h-4" />
                {downloading === ex.id ? 'Downloading...' : 'Download CSV'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminFinanceExport;
